/**
 * LiveStreamingChatOverlay — the presentational bottom band Flutter draws over
 * the live video: the reversed message list above the composer, with the unread
 * pill / jump-to-bottom button floating over the list's bottom.
 *
 * Flutter sources of truth:
 *  - `listWithReplyActions` (`live_streaming_screen.dart:892-920`) — a
 *    bottom-anchored `Column` (`Positioned(bottom: 0)`) whose inner
 *    `ConstrainedBox(maxHeight: size.height / 2, minHeight: 10)` wraps a scrim
 *    (`AppStyles.bottomOverlayDecorationLiveStreaming`) around
 *    `Column([Expanded(messagesList()), bottomInput()])`. The WHOLE band is
 *    hidden while `_clearScreen || _liveStreamingEnded || _isOpeningChannel`
 *    (`:893`) — the PAGE (Task 10) owns that mount gate, so this component is
 *    only mounted when the chat surface is visible.
 *  - `bottomInput` (`:1291-1296`) — the composer, hidden while `_isFirstLoad ||
 *    _liveStreamingEnded || _isOpeningChannel` (`:1293`). This overlay hides the
 *    composer while `isFirstLoad`.
 *  - `unreadMessageWidget` (`:1637-1704`) + `scrolledUpWidget` (`:1706-1735`) —
 *    the tappable unread pill (`_unreadMessageReceived && _unreadListItems
 *    .isNotEmpty`) and the jump-to-bottom button (`!_unreadMessageReceived &&
 *    _isScrolledUp`); the exact predicate split the reused `CommunityUnreadPill`
 *    already implements.
 *  - `_scrollDown` (`:842-852`) — animate to the newest message + flush the
 *    unread bucket (the pill/jump `onScrollDown` action).
 *
 * Purely presentational: it owns the list's imperative
 * `LiveStreamingChatListHandle` ref (so the post-send `scrollToTopToken` and a
 * pill/jump tap can snap to the newest message) but holds NO hook state — chat
 * state lives in the Task-7 `useLiveStreamingChat` hook on the page body, so the
 * page can safely unmount this on clear-screen without losing it.
 *
 * Layout note (web realization of the Flutter `Column`): Flutter draws the whole
 * thing as a bottom-anchored `Column([lastGift(), ConstrainedBox(scrim + list +
 * input)])` (`:899-917`), the last-gift banner sitting ABOVE the scrim band. The
 * web mirrors that with a TRANSPARENT bottom-anchored flex column whose first
 * child is the shared `LastGiftOverlay` and whose second child is the scrim band.
 * The band is a flex column capped at half the viewport height (Flutter `:904-906`)
 * over a translucent scrim (`theme.liveStreamingBottomOverlay`, UI latitude). Its
 * middle region is a `position: relative` wrapper (NOT itself a scroll container)
 * holding two siblings: the message list inside its own `overflow-y: auto` scroll
 * box (so the inner `VirtualList` auto-detects that box as the scroll element
 * rather than the window), and the `CommunityUnreadPill` pinned as an `absolute`
 * overlay over the region's bottom. The pill is a sibling of — not a descendant
 * of — the scroll box, so it stays glued to the visible bottom instead of
 * scrolling with the messages (the same reason `CommunityPage` floats the pill
 * outside the scrolled list). The composer is the next flex item, rendered only
 * when `!isFirstLoad`.
 *
 * Pointer events: the transparent OUTER column is `pointerEvents: 'none'` and the
 * scrim BAND re-enables `pointerEvents: 'auto'`, so the thin last-gift strip above
 * the band falls through to the video's clear-screen toggle (Flutter hit-tests
 * only the painted band, not the last-gift `Padding`), while the band keeps the
 * deterministic pointer ownership the `zIndex: 5` fix relies on (audience taps on
 * the composer / list no longer fall through to clear-screen). The `zIndex: 5`
 * moves to the outer column; a descendant with `pointerEvents: 'auto'` still
 * hit-tests at that stacking level, above the isolated Agora surface.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { LatestLiveGiftInfoEntity } from '@/domain/entities/info/latestLiveGiftInfoEntity';
import { LastGiftOverlay } from '@/presentation/components/common/LastGiftOverlay';
import { CommunityUnreadPill } from '@/presentation/feature/community/components/CommunityUnreadPill';
import {
  LiveStreamingChatList,
  type LiveStreamingChatListHandle,
} from './LiveStreamingChatList';
import { LiveStreamingChatComposer } from './LiveStreamingChatComposer';

export interface LiveStreamingChatOverlayProps {
  /** Newest-first live message list from `useLiveStreamingChat` (lower index = newer). */
  messages: CommunityChatMessageEntity[];
  /**
   * First fetch in flight (`useLiveStreamingChat.isFirstLoad`, Flutter
   * `_isFirstLoad`). Hides the composer while `true` (Flutter `:1293`).
   */
  isFirstLoad: boolean;
  /**
   * Resolved preview string for the newest unread item, or `null` when there
   * are none. Built by the page (Task 10) from the newest unread message.
   */
  unreadPreview: string | null;
  /**
   * Transient ~3 s flag after a message-while-scrolled-away arrival
   * (`useLiveStreamingChat.unreadBannerVisible`, Flutter
   * `_unreadMessageReceived`). Drives the unread-pill branch of the reused
   * `CommunityUnreadPill`.
   */
  unreadBannerVisible: boolean;
  /**
   * True iff the user is scrolled away from the bottom of the list
   * (`useLiveStreamingChat.isScrolledUp`, Flutter `_isScrolledUp`). Drives the
   * jump-to-bottom button fallback.
   */
  isScrolledUp: boolean;
  /**
   * Current unread-item count (`useLiveStreamingChat.unreadItems.length`),
   * surfaced as the pill's `data-qa-value` for the qa-tester.
   */
  unreadItemsCount: number;
  /**
   * Monotonic token ticked on every optimistic insert in
   * `useLiveStreamingChat.sendText` (Flutter animate-to-newest on own send,
   * `:805-809`). On change (past the initial firing) the overlay snaps the
   * reversed list to the newest message.
   */
  scrollToTopToken: number;
  /**
   * Monotonic token bumped by the page's `useBasePage.onPopNext` when the live
   * route is re-shown via back. On change (past the initial firing) the overlay
   * snaps the reversed list to the newest message, restoring the scroll position
   * the kept-alive VirtualList loses while hidden (web-only; Flutter's
   * `didPopNext` is a no-op, `live_streaming_screen.dart:267-272`).
   */
  scrollToNewestToken: number;
  /**
   * Send a live text message. Threaded straight through to the composer's
   * `onSendText` — returns the hook's SYNCHRONOUS "send proceeded past all
   * guards" flag: `true` → the composer clears its field; `false` (offline /
   * empty / no current user) → the composer leaves the typed draft intact
   * (Flutter guard-`return`-before-`clear`, `:768-771` / `:773-775` → `:802`).
   */
  onSendText: (text: string) => boolean;
  /** Hook callback — user scrolled to the bottom (newest visible); flushes unread. */
  onScrolledToBottom: () => void;
  /** Hook callback — user scrolled away from the bottom. */
  onScrolledAway: () => void;
  /** Hook callback — top sentinel intersected, load the older page. */
  onLoadMore: () => void;
  /**
   * The transient "last gift" to show above the chat band, or `null` between
   * gifts (`useLiveStreaming.latestLiveGift`, Flutter `lastGift()`, `:922-1056`).
   */
  lastGift: LatestLiveGiftInfoEntity | null;
  /**
   * The last-gift fade opacity (`useLiveStreaming.latestLiveGiftOpacity`, Flutter
   * `_latestLiveGiftOpacity`). A view-model mirror; drives no visible CSS fade.
   */
  lastGiftOpacity: number;
  /**
   * The finished, localized last-gift title — already substituting "You" for the
   * self-sent case (Flutter `"$username sent a gift"`, `:980`). Built by the page.
   */
  lastGiftTitle: string;
  /**
   * True iff the current user is the broadcaster. Threaded straight through to
   * the composer, which renders the audience gift button only when `false`
   * (Flutter `!_isBroadcaster`, `live_streaming_screen.dart:1385`).
   */
  isBroadcaster: boolean;
  /**
   * Audience gift/tip tap. Threaded straight through to the composer's
   * `onGiftTap`. The PAGE owns opening `SendGiftSheet` (hooks/components never
   * open sheets); this overlay just forwards the callback.
   */
  onGiftTap: () => void;
}

export const LiveStreamingChatOverlay: React.FC<LiveStreamingChatOverlayProps> = ({
  messages,
  isFirstLoad,
  unreadPreview,
  unreadBannerVisible,
  isScrolledUp,
  unreadItemsCount,
  scrollToTopToken,
  scrollToNewestToken,
  onSendText,
  onScrolledToBottom,
  onScrolledAway,
  onLoadMore,
  lastGift,
  lastGiftOpacity,
  lastGiftTitle,
  isBroadcaster,
  onGiftTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Imperative handle into the reversed list — drives the post-send snap and the
  // unread-pill / jump-to-bottom tap. In the reversed list, "go to newest" is a
  // scroll-to-bottom (Flutter `animateTo(0, …)`, `:805-809` / `:842-847`).
  const listRef = useRef<LiveStreamingChatListHandle>(null);

  // Tapping the unread pill / jump-to-bottom button: snap the list to the newest
  // message AND flush the unread bucket (Flutter `_scrollDown`, `:842-852`).
  // Mirrors `CommunityPage.handleScrollDown` (`CommunityPage.tsx:194-197`).
  const handleScrollDown = useCallback((): void => {
    listRef.current?.scrollToBottom();
    onScrolledToBottom();
  }, [onScrolledToBottom]);

  // Post-send auto-scroll: `scrollToTopToken` ticks on every optimistic insert in
  // `sendText`; on change snap the reversed list to the newest message (Flutter
  // `animateTo(0, …)` on own send, `:805-809`). Skip the initial firing so we
  // don't fight the list's own first-mount bottom anchor. Mirrors
  // `CommunityPage.tsx:315-330`.
  const hasSeenInitialTokenRef = useRef<boolean>(false);
  useEffect(() => {
    if (!hasSeenInitialTokenRef.current) {
      hasSeenInitialTokenRef.current = true;
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      listRef.current?.scrollToBottom();
    });
    return () => window.cancelAnimationFrame(handle);
  }, [scrollToTopToken]);

  // Pop-back scroll restore: `scrollToNewestToken` ticks when the page returns to
  // this route via back (`useBasePage.onPopNext`). The kept-alive VirtualList loses
  // its inner scroll position while hidden and resets to the top, so snap the
  // reversed list back to the newest message. Skip the initial firing so a fresh
  // mount doesn't fight the list's own first bottom anchor (same guard as the
  // post-send effect above). `'auto'` = instant, no visible scroll animation.
  const hasSeenInitialPopTokenRef = useRef<boolean>(false);
  useEffect(() => {
    if (!hasSeenInitialPopTokenRef.current) {
      hasSeenInitialPopTokenRef.current = true;
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      listRef.current?.scrollToBottom('auto');
    });
    return () => window.cancelAnimationFrame(handle);
  }, [scrollToNewestToken]);

  return (
    <div
      {...qaAttr({ id: 'live-chat-overlay' })}
      style={{
        // Bottom-anchored TRANSPARENT column (Flutter `Column([lastGift(),
        // ConstrainedBox(...)])`, `:899-917`): the last gift sits ABOVE the scrim
        // band. `pointerEvents: 'none'` here so the thin last-gift strip falls
        // through to the video's clear-screen toggle (Flutter hit-tests only the
        // painted band, not the last-gift `Padding`); the inner band re-enables
        // pointer events.
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        // `isolation: 'isolate'` on the video surface contains the Agora player's
        // stacking context; this explicit z-index makes the band the deterministic
        // pointer-event owner above the video, while staying UNDER the broadcaster
        // controls (z 6) and the top-bar band (z 10) — "under the top bar, over the
        // video". Without it, audience taps fell through to clear-screen. A
        // descendant with `pointerEvents: 'auto'` still hit-tests at this stacking
        // level, so moving the z-index up to the transparent column preserves the fix.
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
      }}
    >
      {/* Last gift ABOVE the scrim band (Flutter `Column`'s first child,
          `lastGift()`, `:922-1056`), left-padded 16 (`:941`). `null` gift renders
          nothing. The shared overlay is `pointerEvents: 'none'` so this strip stays
          tap-through to the video beneath it. */}
      <LastGiftOverlay
        gift={lastGift}
        opacity={lastGiftOpacity}
        title={lastGiftTitle}
        qaId="live-streaming-last-gift"
        style={{ paddingLeft: responsive.spacing.base }}
      />

      {/* Scrim band — the capped, translucent chat band (Flutter
          `ConstrainedBox(maxHeight: size.height / 2)` + scrim, `:904-917`). `50vh`
          mirrors `CommunityUnreadPill`'s existing `50vw` viewport-fraction
          precedent. Re-enables `pointerEvents: 'auto'` so it owns taps on the
          composer / list (the z-index-5 fix); the scrim is a theme gradient (no
          hardcoded color) so the rows read over the always-dark video. */}
      <div
        style={{
          maxHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          background: theme.liveStreamingBottomOverlay,
          pointerEvents: 'auto',
        }}
      >
        {/* List region — grows to fill the band up to the 50vh cap (Flutter
            `Expanded(child: messagesList())`, `:913`). A `position: relative`
            wrapper (visible overflow — NOT a scroll container) so the floating
            unread pill pins to its VISIBLE bottom rather than scrolling with the
            messages. The nested column keeps the scroll box in flow so the band
            sizes to content up to the cap. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Scroll box — the actual scroll container. `overflow-y: auto` so the
              inner `VirtualList` auto-detects THIS box (not the window) as its
              scroll element; `min-height: 0` lets it shrink and scroll once the
              band hits the cap. `scrollbar-hide` (global utility in `index.css`)
              keeps scrolling functional while hiding the scrollbar so it does not
              paint over the video — Flutter's `ListView` shows no scrollbar here.
              `paddingBottom` gives breathing room below the last message and above
              the composer (UI latitude — Flutter draws the list flush; web adds
              space between the list and the composer). `safeArea.bottom` is an
              `env()` string, so combine with the numeric `spacing.xl` token via
              `calc()` (never suffix `px` on `safeArea.*`). */}
          <div
            className="scrollbar-hide"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              paddingBottom: `calc(${responsive.safeArea.bottom} + ${responsive.spacing.xl}px)`,
            }}
          >
            <LiveStreamingChatList
              ref={listRef}
              messages={messages}
              onLoadMore={onLoadMore}
              onScrolledToBottom={onScrolledToBottom}
              onScrolledAway={onScrolledAway}
            />
          </div>

          {/* Unread pill / jump-to-bottom button (`unreadMessageWidget` /
              `scrolledUpWidget`, `:1637-1735`). Pinned over the region's visible
              bottom (sibling of the scroll box, so it does not scroll away);
              `pointerEvents: 'none'` so taps fall through to the list, the pill /
              button re-enable pointer events on their own surface. */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: responsive.spacing.lg,
              pointerEvents: 'none',
            }}
          >
            <CommunityUnreadPill
              unreadPreview={unreadPreview}
              unreadBannerVisible={unreadBannerVisible}
              isScrolledUp={isScrolledUp}
              unreadCount={unreadItemsCount}
              onScrollDown={handleScrollDown}
            />
          </div>
        </div>

        {/* Composer (`bottomInput`, `:1291-1296`). Hidden while `isFirstLoad`
            (Flutter `:1293`). The audience gift button + its sheet open are
            threaded through `isBroadcaster` / `onGiftTap` (Task 7). */}
        {!isFirstLoad && (
          <LiveStreamingChatComposer
            onSendText={onSendText}
            isBroadcaster={isBroadcaster}
            onGiftTap={onGiftTap}
          />
        )}
      </div>
    </div>
  );
};
